(function (root, factory) {
  const core = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = core;
  }

  if (root) {
    root.KostkammBrushFinderCore = core;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const STEP_ORDER = ['hair', 'structure', 'length', 'shape', 'hand', 'bristles'];
  const HAIR_TYPES = ['none', 'thin', 'fine', 'strong', 'thick'];
  const STRUCTURES = ['straight', 'wavy', 'curly'];
  const LENGTHS = ['short', 'medium', 'long'];
  const SHAPES = ['narrow', 'oval', 'ergonomic', 'drop', 'curved', 'roundBack'];
  const HANDS = ['right', 'left'];
  const BRISTLES = ['all', 'vegan'];

  function unique(values) {
    return values.filter(function (value, index) {
      return values.indexOf(value) === index;
    });
  }

  function orderedUnion() {
    return unique(
      Array.prototype.concat
        .apply([], arguments)
        .filter(Boolean)
    );
  }

  function toSkuSet(availableSkus, data) {
    if (availableSkus === undefined || availableSkus === null) {
      return new Set(getDeclaredSkus(data));
    }

    if (availableSkus instanceof Set) return availableSkus;
    return new Set(availableSkus);
  }

  function filterSkus(skus, availableSkus, data) {
    const available = toSkuSet(availableSkus, data);
    return skus.filter(function (sku) {
      return available.has(sku);
    });
  }

  function getDeclaredSkus(data) {
    const skus = [];

    data.rows.forEach(function (row) {
      Object.keys(row.shapes).forEach(function (shape) {
        Object.keys(row.shapes[shape]).forEach(function (bucket) {
          skus.push.apply(skus, row.shapes[shape][bucket]);
        });
      });
    });

    return unique(skus);
  }

  function rowMatches(row, answers) {
    if (answers.hair && row.hairType !== answers.hair) return false;
    if (answers.structure && row.structure !== answers.structure) return false;
    if (answers.length && row.length !== answers.length) return false;
    return true;
  }

  function getMatchingRows(data, answers) {
    return data.rows.filter(function (row) {
      return rowMatches(row, answers || {});
    });
  }

  function getShapeBuckets(rows, shape, availableSkus, data) {
    const buckets = {
      standard: [],
      left: [],
      vegan: [],
    };

    rows.forEach(function (row) {
      const shapeData = row.shapes[shape] || {};
      buckets.standard = orderedUnion(buckets.standard, shapeData.standard || []);
      buckets.left = orderedUnion(buckets.left, shapeData.left || []);
      buckets.vegan = orderedUnion(buckets.vegan, shapeData.vegan || []);
    });

    Object.keys(buckets).forEach(function (bucket) {
      buckets[bucket] = filterSkus(buckets[bucket], availableSkus, data);
    });

    return buckets;
  }

  function optionLeadsToResults(data, answers, step, value, availableSkus) {
    const candidate = Object.assign({}, answers, { [step]: value });
    const rows = getMatchingRows(data, candidate);

    return SHAPES.some(function (shape) {
      const buckets = getShapeBuckets(rows, candidate.shape || shape, availableSkus, data);
      return buckets.standard.length + buckets.left.length + buckets.vegan.length > 0;
    });
  }

  function sameList(left, right) {
    return left.length === right.length && left.every(function (item, index) {
      return item === right[index];
    });
  }

  function getOptions(data, answers, step, availableSkus) {
    const state = answers || {};

    if (step === 'hair') {
      return HAIR_TYPES.filter(function (value) {
        return optionLeadsToResults(data, {}, step, value, availableSkus);
      });
    }

    if (!state.hair) return [];

    if (step === 'structure') {
      if (state.hair === 'none') return [];
      return STRUCTURES.filter(function (value) {
        return optionLeadsToResults(data, { hair: state.hair }, step, value, availableSkus);
      });
    }

    if (step === 'length') {
      if (state.hair === 'none' || !state.structure) return [];
      return LENGTHS.filter(function (value) {
        return optionLeadsToResults(
          data,
          { hair: state.hair, structure: state.structure },
          step,
          value,
          availableSkus
        );
      });
    }

    const rows = getMatchingRows(data, state);

    if (step === 'shape') {
      if (state.hair !== 'none' && (!state.structure || !state.length)) return [];
      return SHAPES.filter(function (shape) {
        const buckets = getShapeBuckets(rows, shape, availableSkus, data);
        return buckets.standard.length + buckets.left.length + buckets.vegan.length > 0;
      });
    }

    if (!state.shape) return [];
    const buckets = getShapeBuckets(rows, state.shape, availableSkus, data);

    if (step === 'hand') {
      return HANDS.filter(function (hand) {
        return hand === 'left'
          ? buckets.left.length > 0
          : buckets.standard.length + buckets.vegan.length > 0;
      });
    }

    if (step === 'bristles') {
      const handOptions = getOptions(data, state, 'hand', availableSkus);
      const effectiveHand = state.hand || handOptions[0] || 'right';
      if (effectiveHand === 'left') return [];

      const all = orderedUnion(buckets.standard, buckets.vegan);
      if (!buckets.vegan.length || sameList(all, buckets.vegan)) return [];
      return BRISTLES;
    }

    return [];
  }

  function isQuestionApplicable(data, answers, step, availableSkus) {
    return getOptions(data, answers, step, availableSkus).length >= 2;
  }

  function getQuestionSteps(data, answers, availableSkus) {
    const state = {};
    const source = answers || {};
    const steps = [];

    STEP_ORDER.forEach(function (step) {
      const options = getOptions(data, state, step, availableSkus);
      if (options.length >= 2) {
        steps.push(step);
        if (options.includes(source[step])) state[step] = source[step];
      } else if (options.length === 1) {
        state[step] = options[0];
      }
    });

    return steps;
  }

  function sanitizeAnswers(data, answers, availableSkus) {
    const clean = {};
    const source = answers || {};

    STEP_ORDER.forEach(function (step) {
      const options = getOptions(data, clean, step, availableSkus);
      if (options.length >= 2 && options.includes(source[step])) {
        clean[step] = source[step];
      }
    });

    return clean;
  }

  function getNextStep(data, answers, availableSkus) {
    const clean = sanitizeAnswers(data, answers, availableSkus);

    for (let index = 0; index < STEP_ORDER.length; index += 1) {
      const step = STEP_ORDER[index];
      const options = getOptions(data, clean, step, availableSkus);
      if (options.length >= 2 && !clean[step]) return step;
      if (options.length === 1) clean[step] = options[0];
    }

    return null;
  }

  function getPriorAnswers(answers, currentStep) {
    const prior = {};
    const currentIndex = STEP_ORDER.indexOf(currentStep);

    STEP_ORDER.forEach(function (step, index) {
      if (index < currentIndex && answers && answers[step]) {
        prior[step] = answers[step];
      }
    });

    return prior;
  }

  function getProgress(data, answers, currentStep, availableSkus) {
    const clean = sanitizeAnswers(data, answers, availableSkus);
    const prior = getPriorAnswers(clean, currentStep);
    const completed = Object.keys(prior).length;
    const current = completed + (currentStep ? 1 : 0);

    const matchingRoutes = enumerateRoutes(data, availableSkus).filter(function (route) {
      return Object.keys(prior).every(function (step) {
        return route[step] === prior[step];
      });
    });

    let total = STEP_ORDER.length;
    if (matchingRoutes.length) {
      total = matchingRoutes.reduce(function (max, route) {
        return Math.max(max, getQuestionSteps(data, route, availableSkus).length);
      }, 0);
    }

    total = Math.max(total, current, 1);

    // Fill only completed answers so the current question still leaves the bar open.
    // Full = 100% only after the last answer (results view).
    const ratio = currentStep ? completed / total : 1;

    return {
      current: current,
      total: total,
      ratio: ratio,
    };
  }

  function resolveAnswers(data, answers, availableSkus) {
    const resolved = sanitizeAnswers(data, answers, availableSkus);

    STEP_ORDER.forEach(function (step) {
      const options = getOptions(data, resolved, step, availableSkus);
      if (options.length === 1) resolved[step] = options[0];
    });

    return resolved;
  }

  function clearAfter(answers, step) {
    const index = STEP_ORDER.indexOf(step);
    const kept = {};

    STEP_ORDER.slice(0, index + 1).forEach(function (key) {
      if (answers[key]) kept[key] = answers[key];
    });

    return kept;
  }

  function getResultSkus(data, answers, availableSkus) {
    const state = resolveAnswers(data, answers, availableSkus);
    if (getNextStep(data, state, availableSkus)) return [];

    const rows = getMatchingRows(data, state);
    if (!state.shape || !rows.length) return [];

    const buckets = getShapeBuckets(rows, state.shape, availableSkus, data);
    const handOptions = getOptions(data, state, 'hand', availableSkus);
    const effectiveHand = state.hand || handOptions[0] || 'right';

    if (effectiveHand === 'left') return buckets.left;
    if (state.bristles === 'vegan') return buckets.vegan;
    return orderedUnion(buckets.standard, buckets.vegan);
  }

  function resolveProducts(skus, products) {
    const resolved = [];
    const seenProducts = new Set();

    skus.forEach(function (sku) {
      products.forEach(function (product) {
        if (!product.skus.includes(sku) || seenProducts.has(product.id)) return;
        seenProducts.add(product.id);
        resolved.push({
          product: product,
          matchedSku: sku,
        });
      });
    });

    return resolved;
  }

  function serializeState(answers) {
    const parts = ['v1'];

    STEP_ORDER.forEach(function (step) {
      if (answers[step]) {
        parts.push(step + '=' + encodeURIComponent(answers[step]));
      }
    });

    return parts.join(';');
  }

  function parseState(value) {
    if (!value || typeof value !== 'string') return {};
    const parts = value.split(';');
    if (parts.shift() !== 'v1') return {};

    const answers = {};
    parts.forEach(function (part) {
      const separator = part.indexOf('=');
      if (separator < 1) return;
      const key = part.slice(0, separator);
      if (!STEP_ORDER.includes(key)) return;
      try {
        answers[key] = decodeURIComponent(part.slice(separator + 1));
      } catch (_error) {
        // Ignore malformed URL components.
      }
    });
    return answers;
  }

  function enumerateRoutes(data, availableSkus) {
    const routes = [];

    data.rows.forEach(function (row) {
      SHAPES.forEach(function (shape) {
        const base = {
          hair: row.hairType,
          shape: shape,
        };
        if (row.structure) base.structure = row.structure;
        if (row.length) base.length = row.length;

        const buckets = getShapeBuckets([row], shape, availableSkus, data);
        if (buckets.standard.length + buckets.vegan.length) {
          const all = orderedUnion(buckets.standard, buckets.vegan);
          routes.push(Object.assign({}, base, { hand: 'right', bristles: 'all' }));
          if (buckets.vegan.length && !sameList(all, buckets.vegan)) {
            routes.push(Object.assign({}, base, { hand: 'right', bristles: 'vegan' }));
          }
        }
        if (buckets.left.length) {
          routes.push(Object.assign({}, base, { hand: 'left' }));
        }
      });
    });

    return routes;
  }

  function getDiagnostics(data, products) {
    const declared = getDeclaredSkus(data);
    const declaredSet = new Set(declared);
    const owners = {};

    products.forEach(function (product) {
      product.skus.forEach(function (sku) {
        if (!owners[sku]) owners[sku] = [];
        owners[sku].push(product.id);
      });
    });

    return {
      missing: declared.filter(function (sku) {
        return !owners[sku] || !owners[sku].length;
      }),
      duplicate: Object.keys(owners).filter(function (sku) {
        return owners[sku].length > 1;
      }),
      extra: Object.keys(owners).filter(function (sku) {
        return !declaredSet.has(sku);
      }),
    };
  }

  return Object.freeze({
    schemaVersion: 1,
    stepOrder: STEP_ORDER.slice(),
    hairTypes: HAIR_TYPES.slice(),
    structures: STRUCTURES.slice(),
    lengths: LENGTHS.slice(),
    shapes: SHAPES.slice(),
    hands: HANDS.slice(),
    bristles: BRISTLES.slice(),
    unique: unique,
    orderedUnion: orderedUnion,
    getDeclaredSkus: getDeclaredSkus,
    getMatchingRows: getMatchingRows,
    getShapeBuckets: getShapeBuckets,
    getOptions: getOptions,
    isQuestionApplicable: isQuestionApplicable,
    getQuestionSteps: getQuestionSteps,
    sanitizeAnswers: sanitizeAnswers,
    resolveAnswers: resolveAnswers,
    getNextStep: getNextStep,
    getPriorAnswers: getPriorAnswers,
    getProgress: getProgress,
    clearAfter: clearAfter,
    getResultSkus: getResultSkus,
    resolveProducts: resolveProducts,
    serializeState: serializeState,
    parseState: parseState,
    enumerateRoutes: enumerateRoutes,
    getDiagnostics: getDiagnostics,
  });
});
